import { useQueryClient } from "@tanstack/react-query";
import { Button, message } from "antd";
import React, { CSSProperties, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ChevronDownIcon, ChevronUpIcon, PinIcon, UnPinIcon } from "../../../assets/svg";
import { formatDateByType } from "../../libs/utils";
import { Note as NoteType, useUpdateNote } from "./api";

const COLLAPSED_HEIGHT = 17;

type NoteProps = {
  data: NoteType;
};

export const Note = ({ data }: NoteProps) => {
  const { noteId, note, noteBy, createdAt, isPinned } = data;

  const queryClient = useQueryClient();
  const { bookingDetails } = useSelector((state: any) => state.newBooking);
  const moduleId = bookingDetails?.leaseId;

  const { updateNote } = useUpdateNote({
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notes", moduleId] }),
    onError: () => message.error("Error occurred while updating note"),
  });

  const contentRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [maxHeight, setMaxHeight] = useState(COLLAPSED_HEIGHT);
  const [isClamped, setIsClamped] = useState(true);

  useEffect(() => {
    const element = contentRef.current;
    if (!element) return;

    setMaxHeight(isExpanded ? element.scrollHeight : COLLAPSED_HEIGHT);
  }, [isExpanded]);

  const handlePinClick = async () => {
    await updateNote({ noteId, isPinned: !isPinned });
  };

  const handleToggle = () => {
    if (isExpanded) {
      setIsExpanded(false);

      setTimeout(() => {
        setIsClamped(true);
      }, 200);
    } else {
      setIsClamped(false);
      setIsExpanded(true);
    }
  };

  const noteTextStyle: CSSProperties = {
    margin: 0,
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    overflow: isClamped ? "hidden" : "visible",
    textOverflow: "ellipsis",
    WebkitLineClamp: isClamped ? 1 : "unset",
    transition: "all 0.2s ease",
  };

  return (
    <div
      style={{
        padding: "1.5rem",
        display: "flex",
        gap: "0.5rem",
        justifyContent: "space-between",
        alignItems: "start",
      }}
    >
      <div>
        <div
          ref={contentRef}
          style={{ lineHeight: "normal", maxHeight, overflow: "hidden", transition: "max-height 0.3s ease" }}
        >
          <p style={noteTextStyle}>{note}</p>
        </div>

        <p style={{ margin: "0.25rem 0 0", fontSize: 12, color: "var(--blue-600)" }}>
          <span>{`${formatDateByType(createdAt, "datetime")} by `}</span>
          <span style={{ fontWeight: 700 }}>{noteBy}</span>
        </p>
      </div>

      <div style={{ display: "flex", gap: "0.25rem" }}>
        <Button type="text" style={{ borderRadius: 6, padding: 4 }} onClick={handlePinClick}>
          {isPinned ? <PinIcon /> : <UnPinIcon />}
        </Button>

        <Button type="text" style={{ borderRadius: 6, padding: 4 }} onClick={handleToggle}>
          {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </Button>
      </div>
    </div>
  );
};
